import React from "react";
import {Link} from "react-router";
import Icon from "../Icon";
import svgPlace from "../../svgs/place.svg";
import svgOM from '../../svgs/om.svg';
import dollars from "../../dollar-format";
import SortTable from "../SortTable";
import {dateISOtoUS} from "../../date-converter";
import { pricePerFoot } from "../CRUDProperty/CRUDProperty";
import {getCurrentAccess} from "../../access";
import {canLinkToProperty} from "../../access-levels";
import {executeQuery} from "../../requests/executeQuery";
import {executeQueryBatched} from "../../requests/executeQueryBatched";
import {GET_OWNERSHIP_INVESTORS, GET_PROPERTY_OWNERSHIPS} from "../../consts/queries";
import {propertyOrder} from "../../filter";
import { effectiveLeaseExpirationDate, parseArcimedesDate } from "../../utils/leaseExpiration";
import { isNegotiationStatusFilter, normalizeListQuery, peakStatusOf, TARGET_LIST_MATURING_LOANS } from "../../utils";
import { hasValidCoords, canGeocode } from "../../utils/geocode";
import ParcelMapLink, { placeOrSay } from "../ParcelMapLink";
import { collidingLastNames, agentColumnLabel } from "./agentColumnName";
import DeceasedPill, { isDeceased } from "../DeceasedPill/DeceasedPill";
import { STATUS_FLAG_TITLES } from "./statusFlagKind";
import StatusFlag from "./StatusFlag";

// The column lower-cases every city and lets CSS capitalise the first letter,
// which turns McKinney into Mckinney; it is the one city with a second capital.
const listCity = (city) => (/^mc\s*kinney$/i.test(String(city).trim()) ? 'McKinney' : city.toLowerCase());

function AddressWithGeocode({property, loadingIds, setLoadingIds}) {
    const id = property.ARCIMEDES_ROW_ID;
    const hasCoords = hasValidCoords(property);
    const hasAddress = canGeocode(property);
    const isLoading = loadingIds[id];

    const handleClick = async (e) => {
        if (hasCoords) return;
        if (!hasAddress) {
            e.preventDefault();
            alert('Cannot geocode: Address, City, or State is missing.');
            return;
        }
        e.preventDefault();
        setLoadingIds(prev => ({...prev, [id]: true}));
        const result = await placeOrSay(property);
        setLoadingIds(prev => ({...prev, [id]: false}));
        if (result) {
            window.open(arc.path.base + "map/" + id + "?zoom=18&mapTypeId=satellite", '_blank');
        }
    };

    return (
        <Link target="_blank" to={{pathname: arc.path.base + "map/" + id, query: {zoom: 18, mapTypeId: "satellite"}}} onClick={handleClick}>
            {property.ADDRESS}
        </Link>
    );
}

function AddressGoogleMapsLink({property}) {
    const query = hasValidCoords(property)
        ? `${property.LATITUDE},${property.LONGITUDE}`
        : encodeURIComponent([property.ADDRESS, property.CITY, property.STATE].filter(Boolean).join(', '));
    return (
        <a href={`https://www.google.com/maps/search/?api=1&query=${query}`} target="_blank" rel="noopener noreferrer">
            {property.ADDRESS}
        </a>
    );
}

const parseStatusList = (statusValue) => {
    if (!statusValue || typeof statusValue !== 'string') return [];
    return statusValue.split(',').map((s) => s.trim()).filter(Boolean);
};

function integer(value) {
    if (value === undefined || value === null || value === "") return "";
    return Math.round(Number(value)).toLocaleString();
}

function percent(value, withDecimals = true) {
    if (value === undefined || value === null || value === "") return "";
    if (withDecimals)
        return Number(value).toFixed(2) + "%";
    return parseInt(value) + "%";
}

const ListTable = (props) => {
    // The member's SUMMIT reach as the screen states it: Listing asks the
    // member's permissions; a lender's property list shows every column.
    const {everySummitRecord, ownDealsOnly, lastVisited} = props;
    // Keep prior list filters (PEAK_STATUS, team members, etc.) intact for sort links;
    // only normalize legacy owner sort codes. Normalized to the internal spelling
    // so the column codes below match; SortTable re-Peaks it for the links.
    const locationQuery = props.location.query || {};
    const query = normalizeListQuery(locationQuery);
    const isHSC = query.SOURCE === 'HSC' || query.SOURCE === 'PEAK';
    const isDB = query.SOURCE === 'DB';
    const statusList = parseStatusList(query.STATUS);
    const isClosed = query.CLOSE_OF_ESCROW_MIN || query.CLOSE_OF_ESCROW_MAX || query.STATUS === 'Closed' || peakStatusOf(query) === 'Closed' || statusList.includes('Closed') || query.SUMMIT_YTD || query.SUMMIT_CLOSINGS;
    const isUnderContract = query.STATUS && query.STATUS === "Under Contract";
    const isTerritory = (query.TERRITORY_LISTING_AGENT_TEAM || query.PRODUCT_TYPE || query.TERRITORY || query.TERRITORY_MARKET) ? true : false;
    const hasStatus = query.STATUS ? true : false;
    const propertyLinkState = React.useCallback((index) => ({
        index,
        fromList: true,
        listSearch: props.location && props.location.search ? props.location.search : window.location.search,
    }), [props.location]);
    const [ownerLinksState, setOwnerLinksState] = React.useState({});
    const fetchedOwnersRef = React.useRef(new Set());
    const isMountedRef = React.useRef(true);
    
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchOwnerLinksForProperty = React.useCallback(async (propertyId) => {
        if (!propertyId) return;

        setOwnerLinksState(prev => {
            if (prev[propertyId]) return prev;
            return {...prev, [propertyId]: {status: 'loading', links: []}};
        });

        try {
            const ownershipRes = await executeQueryBatched({
                queryName: GET_PROPERTY_OWNERSHIPS,
                options: {
                    param: {
                        PROPERTY_ID: String(propertyId),
                        limit: "9999",
                        offset: "0"
                    }
                }
            });

            if (!isMountedRef.current) return;

            if (!ownershipRes || !ownershipRes.success || !Array.isArray(ownershipRes.data) || ownershipRes.data.length === 0) {
                setOwnerLinksState(prev => ({
                    ...prev,
                    [propertyId]: {status: 'loaded', links: []}
                }));
                return;
            }

            const investorResponses = await Promise.all(
                ownershipRes.data.map((ownership) =>
                    executeQueryBatched({
                        queryName: GET_OWNERSHIP_INVESTORS,
                        options: {
                            param: {
                                PROPERTY_OWNERSHIP_ID: String(ownership.ARCIMEDES_ROW_ID),
                                limit: "9999",
                                offset: "0"
                            }
                        }
                    }).then(res => (res && res.success && Array.isArray(res.data)) ? res.data : []).catch(() => [])
                )
            );

            if (!isMountedRef.current) return;

            const investors = investorResponses.flat();
            const seen = new Set();
            const links = investors.reduce((acc, investor) => {
                const investorId = investor && investor.INVESTOR_ID;
                const crmLink = investor && investor.LEAD_ID ? `https://accounts.kore.co/crm/lead/${investor.LEAD_ID}` : null;
                if (!investorId || seen.has(investorId)) {
                    return acc;
                }
                seen.add(investorId);
                acc.push({
                    url: crmLink || `${arc.path.base}investor/${investorId}`,
                    label: investor.NAME || investor.INVESTOR_NAME || `Investor ${investorId}`,
                    DECEASED: investor.DECEASED,
                    SECOND_CONTACT_ID: investor.SECOND_CONTACT_ID,
                    SECOND_CONTACT_NAME: investor.SECOND_CONTACT_NAME,
                });
                return acc;
            }, []);

            setOwnerLinksState(prev => ({
                ...prev,
                [propertyId]: {status: 'loaded', links}
            }));
        } catch (error) {
            if (!isMountedRef.current) return;
            setOwnerLinksState(prev => ({
                ...prev,
                [propertyId]: {status: 'error', links: []}
            }));
        }
    }, []);

    React.useEffect(() => {
        if (!Array.isArray(props.listings)) return;
        const missingPropertyIds = [];

        props.listings.forEach((listing) => {
            const propertyId = listing && listing.ARCIMEDES_ROW_ID;
            const hasInlineOwnerLinks = Array.isArray(listing && listing.owner_links) && listing.owner_links.length > 0;
            if (!propertyId || fetchedOwnersRef.current.has(propertyId) || hasInlineOwnerLinks) return;
            fetchedOwnersRef.current.add(propertyId);
            missingPropertyIds.push(propertyId);
        });

        if (!missingPropertyIds.length) return;

        // Fired together on purpose: the transport queues calls that share a
        // URL into one Arc envelope, so chunking would only buy extra round
        // trips. A full page of rows costs two requests instead of one each.
        Promise.all(missingPropertyIds.map(fetchOwnerLinksForProperty));
    }, [props.listings, fetchOwnerLinksForProperty]);

    const renderOwnerLinksCell = React.useCallback((el) => {
        const propertyId = el && el.ARCIMEDES_ROW_ID;
        const state = propertyId ? ownerLinksState[propertyId] : undefined;
        const fallbackLinks = (el && el.owner_links ? el.owner_links : []).map((link) =>
            typeof link === 'string' ? {url: link, label: link} : link
        ).filter(Boolean);
        

        const renderLinks = (links, idForKey) => {
            const normalizedLinks = links.map(link =>
                typeof link === 'string' ? { url: link, label: link } : link
            );
            const firstLink = normalizedLinks[0]; //This is the most latest owner link
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {/* Two lines at most, as in the Investors list: the tag stays beside the name. */}
                    {firstLink && (
                        <span style={isDeceased(firstLink) ? { whiteSpace: 'nowrap' } : undefined}>
                            <a
                                href={firstLink.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#0066cc', textDecoration: 'underline' }}
                            >
                                {firstLink.label || firstLink.url}
                            </a>
                            <DeceasedPill investor={firstLink} />
                        </span>
                    )}
                    {firstLink && firstLink.SECOND_CONTACT_NAME && (
                        <span style={{ fontSize: '11px', color: '#5f6368', whiteSpace: 'nowrap' }}>
                            2nd Contact: <Link to={{pathname: `${arc.path.base}investor/${firstLink.SECOND_CONTACT_ID}`}}>{firstLink.SECOND_CONTACT_NAME}</Link>
                        </span>
                    )}
                </div>
            );
        }

        if (!propertyId) {
            if (!fallbackLinks.length) {
                return "-";
            }
            return renderLinks(fallbackLinks);
        }

        if (!state) {
            if (fallbackLinks.length) {
                return renderLinks(fallbackLinks, propertyId);
            }
            return <span style={{ color: '#5f6368', fontSize: '12px' }}>Loading owners...</span>;
        }

        if (state.status === 'loading') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {fallbackLinks.length ? renderLinks(fallbackLinks, propertyId) : null}
                    <span style={{ color: '#5f6368', fontSize: '12px' }}>Loading owners...</span>
                </div>
            );
        }

        const linksToRender = state.links && state.links.length ? state.links : fallbackLinks;

        if (state.status === 'error' && !linksToRender.length) {
            return <span style={{ color: '#d93025', fontSize: '12px' }}>Failed to load owners</span>;
        }

        if (!linksToRender.length) {
            return "-";
        }

        return renderLinks(linksToRender, propertyId);
    }, [ownerLinksState]);
    let listings = props.listings;
    if(isHSC && isClosed && !query.order)
    {
        listings = [...props.listings].sort((a,b) => new Date(b.LOAN_CLOSING_DATE) - new Date(a.LOAN_CLOSING_DATE));
    }
    let columns = [{
        name: "",
        render: (el) => el,
    }];

    if (isTerritory) {
        columns.push({
            code: "STATUS",
            name: "Status",
            textAlign: "center",
            render: (el) => el.STATUS_FLAG
                ? <StatusFlag kind={el.STATUS_FLAG} title={STATUS_FLAG_TITLES[el.STATUS_FLAG]}/>
                : "",
        });
    }

    if (isDB) {
        // Database Properties - Standardized columns
        columns.push({
            code: "STATUS",
            name: "Status",
            render: (el) => el.STATUS,
        });

        columns.push({
            code: "CATEGORY",
            name: "Category",
            columnStyle: {
                minWidth: 120
            },
            render: (el) =>
                el.CATEGORY ? el.CATEGORY.toLowerCase() === 'apartments' ? "Multi-Family" : el.CATEGORY[0].toUpperCase() + el.CATEGORY.slice(1) : "",
        });

        columns.push({
            code: "TYPE",
            name: "Type",
            render: (el) => (el.CATEGORY ==='apartments') ? 'MF': el.TYPE,
        });

        columns.push({
            code: "NAME",
            name: "Name",
            columnStyle: {
                minWidth: 250
            },
            render: (el, index) => {
                const name = el.NAME || "Untitled";
                return canLinkToProperty(getCurrentAccess(), el) ? (
                    <Link
                        className="green"
                        to={{pathname: `${arc.path.base}property/${el.ARCIMEDES_ROW_ID}`, state: propertyLinkState(index)}}>
                        {name}
                    </Link>
                ) : (
                    <a
                        href={`https://www.iclimb.io/property/${el.ARCIMEDES_ROW_ID}`}
                        target="_blank">
                        {name}
                    </a>
                )
            },
        });

        columns.push({
            code: "CURRENT_OWNER_NAME",
            name: "Owner",
            columnStyle: {
                minWidth: 200
            },
            render: renderOwnerLinksCell,
        });

        columns.push({
            name: "Parcel",
            textAlign: "center",
            render: (el) => (
                <ParcelMapLink property={el}>
                    <Icon svg={svgPlace}/>
                </ParcelMapLink>
            ),
        });

        columns.push({
            code: "ADDRESS",
            name: "Address",
            columnStyle: {
                minWidth: 230
            },
            render: (el) => el.ADDRESS ? (
                <AddressGoogleMapsLink property={el} />
            ) : null,
        });

        columns.push({
            code: "CITY",
            name: "City",
            className: "capitalize",
            columnStyle: {
                minWidth: 200
            },
            render: (el) => el.CITY ? listCity(el.CITY) : el.CITY,
        });

        columns.push({
            code: "STATE",
            name: "State",
            textAlign: "center",
            render: (el) => el.STATE,
        });

        columns.push({
            code: "MARKET",
            name: "Market",
            columnStyle: {
                minWidth: 150
            },
            render: (el) => el.MARKET || "",
        });

        columns.push({
            code: "COUNTY",
            name: "County",
            columnStyle: {
                minWidth: 150
            },
            className: "capitalize",
            render: (el) => el.COUNTY ? el.COUNTY.toLowerCase() : "",
        });

        columns.push({
            code: "SQFT",
            name: "Sq Ft",
            textAlign: "right",
            render: (el) => integer(el.SQFT),
        });

        columns.push({
            code: "YEAR_BUILT",
            name: "Year Built",
            textAlign: "center",
            render: (el) => el.YEAR_BUILT,
        });

        columns.push({
            code: "TRANS_DATE",
            name: "Transaction Date",
            textAlign: "center",
            render: (el) => el.TRANS_DATE ? dateISOtoUS(el.TRANS_DATE) : "",
        });
    } else if (isHSC) {
        // On the Maturing Loans target list the date that matters is the one
        // the list is filtered on; Courting Since says nothing about when a
        // loan comes due. The link carries `type`, the filter panel carries
        // the explicit window, so both forms count.
        const isMaturingLoans = query.type === TARGET_LIST_MATURING_LOANS
            || !!query.MATURITY_DATE_MIN || !!query.MATURITY_DATE_MAX;
        columns.push(isMaturingLoans ? {
            code: "MATURITY_DATE",
            name: "Maturity Date",
            render: (el) => el.MATURITY_DATE ? dateISOtoUS(el.MATURITY_DATE) : "",
        } : {
            code: "COURTING_SINCE",
            name: "Date",
            textAlign: "center",
            render: (el) => el.COURTING_SINCE ? dateISOtoUS(el.COURTING_SINCE) : "",
        })
        columns.push({
            code: "HSC_STATUS",
            name: "Status",
            render: (el) => el.HSC_STATUS,
        })
        if (isClosed) {
            columns.push({
                code:"LOAN_CLOSING_DATE",
                name:"Loan Closing Date",
                textAlign: "center",
                render: (el) => el.LOAN_CLOSING_DATE ? dateISOtoUS(el.LOAN_CLOSING_DATE) : ""
            });
            // Columns Push for Close of Escrow
                // columns.push({
                //     code:"CLOSE_OF_ESCROW",
                //     name:"Close of Escrow",
                //     render: (el) => el.CLOSE_OF_ESCROW ? dateISOtoUS(el.CLOSE_OF_ESCROW) : ""
                // });
        }
        columns.push({
            name: "OM",
            render: (el) => el.MEDIA_KIT ?
                <Link target='_blank' to={arc.path.media + el.MEDIA_KIT}><Icon svg={svgOM}/></Link> : null,
        }, {
            code: "NAME",
            name: "Name",
            columnStyle: {
                minWidth: 250
            },
            render: (el, index) => {
                const name = el.NAME || "Untitled";
                return everySummitRecord || canLinkToProperty(getCurrentAccess(), el) ? (
                    <Link
                        className="green"
                        to={{pathname: `${arc.path.base}property/${el.ARCIMEDES_ROW_ID}`, state: propertyLinkState(index)}}>
                        {name}
                    </Link>
                ) : (
                    <a
                        href={`https://www.iclimb.io/property/${el.ARCIMEDES_ROW_ID}`}
                        target="_blank">
                        {name}
                    </a>
                )
            },
        }, {
            code: "CURRENT_OWNER_NAME",
            name: "Owner",
            columnStyle: {
                minWidth: 200
            },
            render: renderOwnerLinksCell,
        }, {
            name: "Parcel",
            textAlign: "center",
            render: (el) => (
                <ParcelMapLink property={el}>
                    <Icon svg={svgPlace}/>
                </ParcelMapLink>
            ),
        }, {
            code: "ADDRESS",
            name: "Address",
            columnStyle: {
                minWidth: 230
            },
            render: (el) => el.ADDRESS ? (
                <AddressGoogleMapsLink property={el} />
            ) : null,
        }, {
            code: "CITY",
            name: "City",
            className: "capitalize",
            columnStyle: {
                minWidth: 200
            },
            render: (el) => el.CITY ? listCity(el.CITY) : el.CITY,
        }, {
            code: "STATE",
            name: "State",
            textAlign: "center",
            render: (el) => el.STATE,
        });

        if (isClosed) {
            columns.push({
                code: "SOLD_PRICE",
                name: "Price at Close",
                textAlign: "right",
                render: (el) => dollars(el.SOLD_PRICE),
            }, {
                code: "CLOSE_CAP_RATE",
                name: "Close Cap Rate",
                textAlign: "center",
                render: (el) => percent(el.CLOSE_CAP_RATE),
            });
        }

        columns.push({
            code: "HSC_PRICE",
            name: "Price",
            textAlign: "right",
            render: (el) => dollars(el.HSC_PRICE),
        }, {
            code: "CAPITALIZATION_RATE",
            name: "Cap Rate",
            textAlign: "center",
            render: (el) => percent(el.CAPITALIZATION_RATE),
        }, {
            code: "LOAN_AMOUNT",
            name: "Loan Amount",
            textAlign: "right",
            render: (el) => dollars(el.LOAN_AMOUNT),
        }, {
            code: "LTV",
            name: "LTV",
            textAlign: "center",
            render: (el) => percent(el.LTV, false),
        }, {
            code: "DOWN_PAYMENT",
            name: "Down Payment",
            textAlign: "right",
            render: (el) => dollars(el.DOWN_PAYMENT),
        }, {
            code: "INTEREST_RATE",
            name: "Rate",
            textAlign: "center",
            render: (el) => percent(el.INTEREST_RATE),
        }, {
            code: "AMORTIZATION",
            name: "Amo",
            textAlign: "center",
            render: (el) => el.AMORTIZATION,
        }, {
            code: "TERM",
            name: "Term",
            textAlign: "center",
            render: (el) => el.TERM,
        }, {
            code: "LENDER_NAME",
            name: "Lender",
            columnStyle: {
                minWidth: 100
            },
            render: (el) => el.LENDER_NAME,
        });
    } else if (isTerritory && !hasStatus) {
        // columns.push({
        //     code: "ACCOUNT_NUMBER",
        //     name: "Acct #",
        //     render: (el) => el.ACCOUNT_NUMBER || "",
        // });
        columns.push({
            code: "CAD_SITE",
            name: "CAD Site",
            render: (el) => el.CAD_SITE ? <a href={el.CAD_SITE} target="_blank">Open CAD</a> : "",
        });
        columns.push({
            code: "NAME",
            name: "Name",
            columnStyle: {
                minWidth: 250
            },
            render: (el, index) => {
                const name = el.NAME || "Untitled";
                return canLinkToProperty(getCurrentAccess(), el) ? (
                    <Link
                        className="green"
                        to={{pathname: `${arc.path.base}property/${el.ARCIMEDES_ROW_ID}`, state: propertyLinkState(index)}}>
                        {name}
                    </Link>
                ) : (
                    <a
                        href={`https://www.iclimb.io/property/${el.ARCIMEDES_ROW_ID}`}
                        target="_blank">
                        {name}
                    </a>
                )
            },
        });
        columns.push({
            code: "CURRENT_OWNER_NAME",
            name: "Owner",
            columnStyle: {
                minWidth: 200
            },
            render: renderOwnerLinksCell,
        });
        columns.push({
            name: "Parcel",
            textAlign: "center",
            render: (el) => (
                <ParcelMapLink property={el}>
                    <Icon svg={svgPlace}/>
                </ParcelMapLink>
            ),
        });
        columns.push({
            code: "ADDRESS",
            name: "Address",
            columnStyle: {
                minWidth: 230
            },
            render: (el) => el.ADDRESS ? (
                <AddressGoogleMapsLink property={el} />
            ) : null,
        });
        columns.push({
            code: "CITY",
            name: "City",
            className: "capitalize",
            columnStyle: {
                minWidth: 200
            },
            render: (el) => el.CITY ? listCity(el.CITY) : el.CITY,
        });
        columns.push({
            code: "STATE",
            name: "State",
            textAlign: "center",
            render: (el) => el.STATE,
        });
        columns.push({
            code: "TRANS_DATE",
            name: "Trans Date",
            textAlign: "center",
            render: (el) => el.TRANS_DATE ? dateISOtoUS(el.TRANS_DATE) : "",
        });
        columns.push({
            code: "SQFT",
            name: "Sq Ft",
            textAlign: "right",
            render: (el) => integer(el.SQFT),
        });
        columns.push({
            code: "ACRES",
            name: "Acres",
            textAlign: "right",
            render: (el) => el.ACRES || "",
        });
        columns.push({
            code: "YEAR_BUILT",
            name: "Year Built",
            textAlign: "center",
            render: (el) => el.YEAR_BUILT,
        });
        columns.push({
            code: "CATEGORY",
            name: "Category",
            columnStyle: {
                minWidth: 120
            },
            render: (el) =>
                el.CATEGORY ? el.CATEGORY.toLowerCase() === 'apartments' ? "Multi-Family" : el.CATEGORY[0].toUpperCase() + el.CATEGORY.slice(1) : "",
        });
        columns.push({
            code: "TYPE",
            name: "Type",
            render: (el) => (el.CATEGORY === 'apartments') ? 'MF' : el.TYPE,
        });
        columns.push({
            code: "SINGLE_TENANT_SUBCATEGORY",
            name: "Tenant Subcategory",
            columnStyle: {
                minWidth: 150
            },
            render: (el) => el.SINGLE_TENANT_SUBCATEGORY || el.MULTI_TENANT_SUBCATEGORY || "",
        });
        columns.push({
            code: "MARKET",
            name: "Market",
            columnStyle: {
                minWidth: 150
            },
            render: (el) => el.MARKET || "",
        });
        columns.push({
            code: "COUNTY",
            name: "County",
            columnStyle: {
                minWidth: 150
            },
            className: "capitalize",
            render: (el) => el.COUNTY ? el.COUNTY.toLowerCase() : "",
        });
    } else {
        if (isClosed) {
            columns.push({
                code: "CLOSE_OF_ESCROW",
                name: "COE",
                sortColumn: "CLOSE_OF_ESCROW",
                render: (el) => el.CLOSE_OF_ESCROW ? dateISOtoUS(el.CLOSE_OF_ESCROW) : "",
            });
        }

        const isNegotiation = isNegotiationStatusFilter(query);

        columns.push({
            code: "STATUS",
            name: "Status",
            sortColumn: `${query.STATUS && query.STATUS==='Active'? 'LISTED_DATE':'STATUS'}`,
            // Don't reserve a fixed width — just keep the longer statuses
            // ("In Negotiation", "Under Contract") from wrapping. The column then
            // sizes to its widest value, staying narrow when statuses are short.
            columnStyle: {
                whiteSpace: "nowrap"
            },
            render: (el) => el.STATUS,
        });

        columns.push({
            code: "CATEGORY",
            name: "Category",
            columnStyle: {
                minWidth: 120
            },
            render: (el) =>
                el.CATEGORY ? el.CATEGORY.toLowerCase() === 'apartments' ? "Multi-Family" : el.CATEGORY[0].toUpperCase() + el.CATEGORY.slice(1) : "",
        });

        columns.push({
            code: "TYPE",
            name: "Type",
            render: (el) => (el.CATEGORY ==='apartments') ? 'MF': el.TYPE,
        });

        if (everySummitRecord) {
            const agentLastNameCollisions = collidingLastNames(listings);
            columns.push({
                code: "AGENT_1_LAST_NAME",
                name: "Agent",
                columnStyle: {
                    minWidth: 120
                },
                render: (el) => agentColumnLabel(el, agentLastNameCollisions),
            });
        }

        columns.push({
            name: "OM",
            render: (el) => el.MEDIA_KIT ?
                <Link target='_blank' to={arc.path.media + el.MEDIA_KIT}><Icon svg={svgOM}/></Link> : null,
        });

        columns.push({
            code: "NAME",
            name: "Name",
            columnStyle: {
                minWidth: 250
            },
            render: (el, index) => {
                const name = el.NAME || "Untitled";
                return canLinkToProperty(getCurrentAccess(), el) ? (
                    <Link
                        className="green"
                        to={{pathname: `${arc.path.base}property/${el.ARCIMEDES_ROW_ID}`, state: propertyLinkState(index)}}>
                        {name}
                    </Link>
                ) : (
                    <a
                        href={`https://www.iclimb.io/property/${el.ARCIMEDES_ROW_ID}`}
                        target="_blank">
                        {name}
                    </a>
                )
            },
        });

        columns.push({
            code: "CURRENT_OWNER_NAME",
            name: "Owner",
            columnStyle: {
                minWidth: 200
            },
            render: renderOwnerLinksCell,
        });
        if (isUnderContract) {
            columns.push({
                code: "EFFECTIVE_DATE",
                name: "Effective Date",
                render: (el) => el.EFFECTIVE_DATE ? dateISOtoUS(el.EFFECTIVE_DATE) : '',
            });
        }
        columns.push({
            name: "Parcel",
            textAlign: "center",
            render: (el) => (
                <ParcelMapLink property={el}>
                    <Icon svg={svgPlace}/>
                </ParcelMapLink>
            ),
        });

        columns.push({
            code: "ADDRESS",
            name: "Address",
            columnStyle: {
                minWidth: 230
            },
            render: (el) => el.ADDRESS ? (
                <AddressGoogleMapsLink property={el} />
            ) : null,
        });

        columns.push({
            code: "CITY",
            name: "City",
            className: "capitalize",
            columnStyle: {
                minWidth: 200
            },
            render: (el) => el.CITY ? listCity(el.CITY) : el.CITY,
        });

        columns.push({
            code: "STATE",
            name: "State",
            textAlign: "center",
            render: (el) => el.STATE,
        });

        if (isNegotiation) {
            columns.push({
                code: "NEGOTIATION_PRICE",
                name: "Neg. Price",
                textAlign: "right",
                render: (el) => dollars(el.NEGOTIATION_PRICE),
            });
            columns.push({
                code: "NEGOTIATION_CAP_RATE",
                name: "Cap Rate",
                textAlign: "center",
                render: (el) => percent(el.NEGOTIATION_CAP_RATE),
            });
        } else if (!isClosed) {
            columns.push({
                code: "LIST_PRICE",
                name: "List Price",
                textAlign: "right",
                render: (el) =>  dollars(el.LIST_PRICE != null ? el.LIST_PRICE : (el.HSC_PRICE != null ? el.HSC_PRICE: null)),
            });
            columns.push({
                code: "CAPITALIZATION_RATE",
                name: "Cap Rate",
                textAlign: "center",
                render: (el) => el.IS_PROFORMA_PRICING=='y' ? percent(el.PROFORMA_CAP_RATE) : percent(el.CAPITALIZATION_RATE )
            });
        }

        if (isClosed) {
            columns.push({
                code: "SOLD_PRICE",
                name: "Price @ Close",
                textAlign: "right",
                render: (el) => dollars(el.SOLD_PRICE),
            });
            columns.push({
                code: "CLOSE_CAP_RATE",
                name: "Cap Rate",
                textAlign: "center",
                render: (el) => percent(el.CLOSE_CAP_RATE),
            });
            columns.push({
                code: "CLOSE_NOI",
                name: "NOI",
                textAlign: "right",
                render: (el) => dollars(el.CLOSE_NOI),
            });
        } else {
            columns.push({
                code: "NET_OPERATING_INCOME",
                name: "NOI",
                textAlign: "right",
                render: (el) =>  el.IS_PROFORMA_PRICING=='y' ? dollars(el.PROFORMA_NOI) : dollars(el.NET_OPERATING_INCOME ),                
            });
        }

        if (isNegotiation) {
            columns.push({
                code: "LIST_PRICE",
                name: "List Price",
                textAlign: "right",
                render: (el) =>  dollars(el.LIST_PRICE != null ? el.LIST_PRICE : (el.HSC_PRICE != null ? el.HSC_PRICE: null)),
            });
            columns.push({
                code: "CAPITALIZATION_RATE",
                name: "Cap Rate",
                textAlign: "center",
                render: (el) => el.IS_PROFORMA_PRICING=='y' ? percent(el.PROFORMA_CAP_RATE) : percent(el.CAPITALIZATION_RATE )
            });
        }

        const isActive = query.STATUS === 'Active' || peakStatusOf(query) === 'Active';
        if (!isNegotiation && everySummitRecord && !isActive && !isClosed) {
            columns.push({
                code: "SELLERS_LAST_NAME",
                name: "Seller",
                render: (el) => {
                    return el.SELLERS_LAST_NAME || "";
                },
            });
        }

        columns.push({
            code: "SQFT",
            name: "SQFT",
            textAlign: "right",
            render: (el) => integer(el.SQFT),
        });

        columns.push({
            code: "RENT_PER_SQFT",
            name: "Rent/SQFT",
            textAlign: "right",
            render: (el) => dollars(el.RENT_PER_SQFT, true),
        });

        if (isClosed) {
            columns.push({
                code: "CLOSE_PRICE_PER_SQFT",
                name: "PSF",
                textAlign: "right",
                render: (el) => pricePerFoot(el.SOLD_PRICE, el.SQFT, true),
            });
        } else if (isNegotiation) {
            columns.push({
                code: "NEGOTIATION_PRICE_PER_SQFT",
                name: "PSF",
                textAlign: "right",
                render: (el) => {
                    if (el.NEGOTIATION_PRICE_PER_SQFT != null && el.NEGOTIATION_PRICE_PER_SQFT !== '') {
                        return dollars(el.NEGOTIATION_PRICE_PER_SQFT, true);
                    }
                    return pricePerFoot(el.NEGOTIATION_PRICE, el.SQFT, true);
                },
            });
        } else {
            columns.push({
                code: "PRICE_PER_SQFT",
                name: "PSF",
                textAlign: "right",
                columnStyle: {
                    padding: "0 32px"
                },
                render: (el) =>  pricePerFoot(el.SOURCE === 'HSC' ? el.HSC_PRICE : el.LIST_PRICE, el.SQFT, true),
            });
        }

        if (!isClosed && !isNegotiation) {
            columns.push({
                code: "COUNTY",
                name: "County",
                columnStyle: {
                    minWidth: 120
                },
                className: "capitalize",
            render: (el) => el.COUNTY ? el.COUNTY.toLowerCase() : "",
            });
        }

        if (query.TYPE === "STNL" || query.STATUS === 'Closed' || query.STATUS === 'Active'  || statusList.includes('Closed') || statusList.includes('Active')) {
            columns.push({
                code: "LEASE_EXPIRATION_DATE",
                name: "Remaining Term",
                render: (el) => {
                    const ex = effectiveLeaseExpirationDate(el);
                    if (!ex) {
                        return "";
                    }
                    let diff;

                    if (el.STATUS && el.STATUS.toLowerCase() === "closed") {
                        const escrowDate = parseArcimedesDate(el.CLOSE_OF_ESCROW);
                        if (!escrowDate) {
                            return "";
                        }
                        diff = ex - escrowDate;
                    } else {
                        const today = new Date();
                        diff = ex - today;
                    }

                    if (diff !== diff) {
                        return "";
                    }

                    if (diff <= 0) {
                        return "Expired";
                    }

                    return (
                        Math.round((diff / (1000 * 60 * 60 * 24 * 365)) * 10) / 10 +
                        " years"
                    );
                },
            });

            columns.push({
                code: "LEASE_TYPE",
                name: "Lease",
                render: (el) => el.LEASE_TYPE,
            });
        }

        columns.push({
            code: "YEAR_BUILT",
            name: "Year Built",
            textAlign: "center",
            render: (el) => el.YEAR_BUILT,
        });

        if ((query.CATEGORY === "retail" || query.CATEGORY === "office") && query.TYPE === "MT") {
            columns.push({
                code: "VACANCY",
                name: "Occupancy",
                textAlign: "right",
                render: (el) => (el.VACANCY ? el.VACANCY + "%" : ""),
            });
        }
    }

    return (
        <div className="ListTable">
            <SortTable
                columns={columns}
                items={listings}
                query={query}
                selectedAll={props.selectedAll}
                classNameModifier={ownDealsOnly ? 'properties-senior' : 'properties'}
                toggleCheckbox={props.toggleCheckbox}
                toggleAllCheckbox={props.toggleAllCheckbox}
                lastVisited={lastVisited}
                columnOrder={propertyOrder}
            />
        </div>
    );
};

export default ListTable;
